<template>
  <div>
    <router-link to="/templates" class="back-link">
      <v-icon name="angle-left" scale="0.8" class="mr-1" />Back to templates
    </router-link>

    <h1 class="template-title">
      {{ $get(template, "name") }}
    </h1>

    <p class="template-price">
      {{ $get(template, "price") | formatPrice }}
    </p>

    <div class="template-meta">
      <p class="template-category">
        <router-link
          v-if="$get(template, 'category.categoryId')"
          :to="`/templates/${$get(template, 'category.categoryId')}`"
        >
          <span class="meta-icon"> <v-icon name="folder"/></span
          >{{ $get(template, "category.label") }}
        </router-link>
      </p>
    </div>

    <hr class="mb-4 md:mt-4 divider md:ml-0" />

    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="template.description"
      class="template-description"
      :class="{ truncated: truncateDescription }"
      v-html="$get(template, 'description')"
    />

    <button
      v-if="template.description"
      class="truncate-button"
      @click="onTruncateClick"
    >
      Read {{ truncateDescription ? "more" : "less" }}
    </button>

    <!-- Features -->
    <div>
      <p class="standard-features-title">
        This template includes:
      </p>

      <div class="standard-features">
        <div class="standard-feature">
          <div class="standard-feature-card">
            <v-icon name="check" scale=".5" class="standard-feature-icon" />
            <span class="standard-feature-label"
              >Customize with your brand</span
            >
          </div>
        </div>
        <div class="standard-feature">
          <div class="standard-feature-card">
            <v-icon name="check" scale=".5" class="standard-feature-icon" />
            <span class="standard-feature-label"
              >Customizable content sections</span
            >
          </div>
        </div>
        <div class="standard-feature">
          <div class="standard-feature-card">
            <v-icon name="check" scale=".5" class="standard-feature-icon" />
            <span class="standard-feature-label"
              >Automatic product importing</span
            >
          </div>
        </div>
        <div class="standard-feature">
          <div class="standard-feature-card">
            <v-icon name="check" scale=".5" class="standard-feature-icon" />
            <span class="standard-feature-label">Automatic blog importing</span>
          </div>
        </div>
        <div class="standard-feature">
          <div class="standard-feature-card">
            <v-icon name="check" scale=".5" class="standard-feature-icon" />
            <span class="standard-feature-label">Mobile-friendly design</span>
          </div>
        </div>
        <div class="standard-feature">
          <div class="standard-feature-card">
            <v-icon name="check" scale=".5" class="standard-feature-icon" />
            <span class="standard-feature-label">Responsive layouts</span>
          </div>
        </div>
      </div>
    </div>

    <CreateCampaignLogic
      v-if="$get(template, 'baseTemplateId')"
      :base-template-id="$get(template, 'baseTemplateId')"
      :base-template-category-id="$get(template, 'category.categoryId')"
    >
      <Button
        slot-scope="{ createCampaign, creatingCampaign }"
        class="primary create-button"
        :loading="creatingCampaign"
        @click="createCampaign"
      >
        {{ creatingCampaign ? "Creating Campaign" : "Select this template" }}
      </Button>
    </CreateCampaignLogic>
  </div>
</template>

<script>
import truncate from "lodash.truncate";
import CreateCampaignLogic from "@/components/Campaigns/CreateCampaignLogic";
import { getAccountId } from "@/lib/auth";

export default {
  name: "TemplateMeta",
  components: { CreateCampaignLogic },
  props: {
    template: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      truncateDescription: true,
    };
  },
  methods: {
    onTruncateClick() {
      if (this.truncateDescription) {
        this.$gtm.trackEvent({
          event: "promo-show-template-description",
          accountId: getAccountId(),
        });
      }

      this.truncateDescription = !this.truncateDescription;
    },
  },
};
</script>

<style scoped>
.template-title {
  @apply leading-tight mb-2 text-center;

  @screen md {
    @apply text-lg text-left mb-2;
  }

  @screen lg {
    @apply text-2xl mb-1;
  }

  @screen xl {
    @apply text-4xl;
  }
}

.template-price {
  @apply capitalize text-center font-lato text-grey-darkest mb-2;

  @screen sm {
    @apply text-lg;
  }

  @screen md {
    @apply text-left text-sm;
  }

  @screen lg {
    @apply text-lg mb-4;
  }

  @screen xl {
    @apply text-xl;
  }
}

.template-category {
  svg {
    @apply relative;
    top: 1px;

    @screen md {
      top: 2px;
    }
  }
}

.template-meta {
  @apply flex flex-wrap justify-center;

  @screen md {
    @apply justify-start mb-3 block;
  }

  p {
    @apply flex items-center text-grey-darker mr-2 text-xs mb-4;

    @screen md {
      @apply mb-0;
    }

    @screen lg {
      @apply text-sm mb-2;
    }
  }

  a {
    @apply text-brand;

    &:hover {
      @apply no-underline text-brand-dark;
    }
  }
}

.template-description {
  @apply leading-normal text-xs mb-0 mx-auto text-center text-support max-w-xs;

  &.truncated {
    overflow: hidden;
    position: relative;
    height: 144px;

    &:after {
      content: "";
      position: absolute;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 55px;
      background: linear-gradient(rgba(255, 255, 255, 0), white 100%);
      pointer-events: none;
    }
  }

  >>> p {
    @apply mb-4;

    &:last-child {
      @apply mb-1;
    }
  }

  @screen md {
    @apply text-left max-w-sm mx-0;
  }

  @screen lg {
    @apply text-sm;
  }
}

.truncate-button {
  @apply text-xxs text-grey-dark outline-none mx-auto block;

  @screen md {
    @apply mx-0;
  }
}

.meta-icon {
  @apply inline-block;
  margin-right: 5px;

  @screen md {
    margin-right: 2px;
    width: 18px;
  }

  @screen lg {
    width: 21px;
  }
}

.create-button {
  @apply mb-4 mx-auto;

  @screen md {
    @apply p-2 px-4 text-sm m-0;
  }

  @screen xl {
    @apply -mt-4;
  }
}

.back-link {
  @apply text-xs mb-2 flex items-center text-grey-dark justify-center;

  @screen md {
    @apply justify-start;
  }
}

.standard-features-title {
  @apply text-left mb-1 text-xs text-support-light;

  @screen sm {
    @apply text-center mb-2 mt-6;
  }

  @screen md {
    @apply text-left mb-1;
  }

  @screen xl {
    @apply mb-3;
  }
}

.standard-features {
  @apply flex flex-wrap -mx-1 mb-4;

  @screen sm {
    @apply max-w-sm mx-auto mb-6;
  }

  @screen md {
    @apply -mx-1;
  }

  @screen xl {
    @apply -mx-2 justify-center;
  }
}

.standard-feature {
  @apply w-full font-light px-1;

  @screen sm {
    @apply w-1/2;
  }

  @screen md {
    @apply w-full;
  }

  @screen xl {
    @apply w-1/2 px-2 mb-4;

    .standard-feature-card {
      @apply flex items-center bg-white p-2 rounded shadow h-full;
    }
  }
}

.standard-feature-icon {
  @apply mr-1 text-brand;

  @screen xl {
    @apply w-3 mr-2;
  }
}

.standard-feature-label {
  @apply text-support-light text-xs;
}
</style>
